import { INTERVAL_SECONDS } from '../../config/timeframes.js';
import { safeNumber } from '../../lib/number.js';
import type { Candle, ChartInterval } from '../../types/market.js';
import { fetchJson } from './http.js';

interface BybitTickerResponse {
  retCode: number;
  retMsg?: string;
  result?: {
    list?: Array<{
      symbol: string;
      lastPrice: string;
      turnover24h: string;
      volume24h: string;
      price24hPcnt?: string;
      openInterest?: string;
      fundingRate?: string;
      bid1Price?: string;
      ask1Price?: string;
    }>;
  };
}

interface BybitKlineResponse {
  retCode: number;
  retMsg?: string;
  result?: { list?: string[][] };
}

export interface ExternalVenueTicker {
  exchange: 'bybit' | 'okx';
  symbol: string;
  price?: number;
  quoteVolume24h?: number;
  change24h?: number;
  openInterest?: number;
  fundingRate?: number;
  bid?: number;
  ask?: number;
}

export const BYBIT_INTERVAL: Record<ChartInterval, string> = {
  '1m': '1',
  '5m': '5',
  '15m': '15',
  '30m': '30',
  '1h': '60',
  '2h': '120',
  '4h': '240',
  '1d': 'D',
  '1w': 'W',
};

export function parseBybitCandles(
  rows: string[][],
  interval: ChartInterval,
  serverNow = Date.now(),
): Candle[] {
  const durationMs = INTERVAL_SECONDS[interval] * 1_000;
  return rows
    .map((row) => {
      const openTime = safeNumber(row[0]);
      const closeTime = openTime + durationMs - 1;
      return {
        openTime,
        closeTime,
        open: safeNumber(row[1]),
        high: safeNumber(row[2]),
        low: safeNumber(row[3]),
        close: safeNumber(row[4]),
        volume: safeNumber(row[5]),
        quoteVolume: safeNumber(row[6]),
        closed: closeTime < serverNow,
      } satisfies Candle;
    })
    .filter((candle) => candle.openTime > 0 && candle.close > 0)
    .sort((left, right) => left.openTime - right.openTime);
}

export async function getBybitTicker(symbol: string): Promise<ExternalVenueTicker | null> {
  const response = await fetchJson<BybitTickerResponse>(
    `https://api.bybit.com/v5/market/tickers?category=linear&symbol=${encodeURIComponent(symbol)}`,
  );
  if (response.retCode !== 0) {
    throw new Error(`Bybit market API error ${response.retCode}: ${response.retMsg ?? 'unknown error'}`);
  }
  const item = response.result?.list?.[0];
  if (!item) return null;
  return {
    exchange: 'bybit',
    symbol: item.symbol,
    price: safeNumber(item.lastPrice),
    quoteVolume24h: safeNumber(item.turnover24h),
    change24h: item.price24hPcnt ? safeNumber(item.price24hPcnt) * 100 : undefined,
    openInterest: item.openInterest ? safeNumber(item.openInterest) : undefined,
    fundingRate: item.fundingRate ? safeNumber(item.fundingRate) : undefined,
    bid: item.bid1Price ? safeNumber(item.bid1Price) : undefined,
    ask: item.ask1Price ? safeNumber(item.ask1Price) : undefined,
  };
}

export async function getBybitCandles(
  symbol: string,
  interval: ChartInterval,
  limit = 300,
  marketKind: 'spot' | 'futures' = 'futures',
  serverNow = Date.now(),
): Promise<Candle[]> {
  const category = marketKind === 'spot' ? 'spot' : 'linear';
  const response = await fetchJson<BybitKlineResponse>(
    `https://api.bybit.com/v5/market/kline?category=${category}&symbol=${encodeURIComponent(symbol)}&interval=${BYBIT_INTERVAL[interval]}&limit=${Math.min(1_000, limit)}`,
  );
  if (response.retCode !== 0) {
    throw new Error(`Bybit kline API error ${response.retCode}: ${response.retMsg ?? 'unknown error'}`);
  }
  return parseBybitCandles(response.result?.list ?? [], interval, serverNow);
}
